import type { Dispatch, SetStateAction } from 'react';
import {
  NAME_FONTS,
  SIGNATURE_FONTS,
  nameFontStack,
  signatureFontStack,
  type InvitationData,
} from '@/types/wedding';

interface Props {
  data: InvitationData;
  setData: Dispatch<SetStateAction<InvitationData>>;
}

export default function InfoForm({ data, setData }: Props) {
  const patch = <K extends keyof InvitationData>(key: K, value: InvitationData[K]) =>
    setData((prev) => ({ ...prev, [key]: value }));

  const patchGroom = (key: keyof InvitationData['groom'], value: string) =>
    setData((prev) => ({ ...prev, groom: { ...prev.groom, [key]: value } }));

  const patchBride = (key: keyof InvitationData['bride'], value: string) =>
    setData((prev) => ({ ...prev, bride: { ...prev.bride, [key]: value } }));

  const patchAccount = (
    side: 'groomAccount' | 'brideAccount',
    key: 'bank' | 'number' | 'holder',
    value: string,
  ) => setData((prev) => ({ ...prev, [side]: { ...prev[side], [key]: value } }));

  const patchTransit = (key: 'car' | 'bus' | 'subway', value: string) =>
    setData((prev) => ({ ...prev, transit: { ...prev.transit, [key]: value } }));

  return (
    <>
      {/* COUPLE */}
      <div className="ed-section">
        <div className="ed-section-head">
          <span className="step">1</span>
          <h2>신랑 · 신부</h2>
          <span className="hint">이름과 부모님, 연락처</span>
        </div>
        <div className="ed-cols">
          <div className="ed-stack">
            <p className="ed-subtitle">
              <i className="ri-men-line" aria-hidden="true" />
              신랑
            </p>
            <div className="ed-field">
              <label className="ed-label" htmlFor="g-name">
                이름
              </label>
              <input
                id="g-name"
                className="ed-input"
                value={data.groom.name}
                onChange={(e) => patchGroom('name', e.target.value)}
                placeholder="김도윤"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="g-parents">
                부모님
              </label>
              <input
                id="g-parents"
                className="ed-input"
                value={data.groom.parents}
                onChange={(e) => patchGroom('parents', e.target.value)}
                placeholder="김정호 · 박선영 의 장남"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="g-phone">
                연락처
              </label>
              <input
                id="g-phone"
                className="ed-input"
                value={data.groom.phone}
                onChange={(e) => patchGroom('phone', e.target.value)}
                placeholder="010-0000-0000"
                inputMode="tel"
              />
            </div>
          </div>

          <div className="ed-stack">
            <p className="ed-subtitle">
              <i className="ri-women-line" aria-hidden="true" />
              신부
            </p>
            <div className="ed-field">
              <label className="ed-label" htmlFor="b-name">
                이름
              </label>
              <input
                id="b-name"
                className="ed-input"
                value={data.bride.name}
                onChange={(e) => patchBride('name', e.target.value)}
                placeholder="이서연"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="b-parents">
                부모님
              </label>
              <input
                id="b-parents"
                className="ed-input"
                value={data.bride.parents}
                onChange={(e) => patchBride('parents', e.target.value)}
                placeholder="이광수 · 최은자 의 장녀"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="b-phone">
                연락처
              </label>
              <input
                id="b-phone"
                className="ed-input"
                value={data.bride.phone}
                onChange={(e) => patchBride('phone', e.target.value)}
                placeholder="010-0000-0000"
                inputMode="tel"
              />
            </div>
          </div>
        </div>
        <div className="ed-stack" style={{ marginTop: 20 }}>
          <div className="ed-field">
            <span className="ed-label">이름 폰트 (메인 신랑 · 신부 이름)</span>
            <p className="ed-font-hint">
              청첩장 상단과 신랑 · 신부 소개에 나오는 이름에 쓸 글씨체를 골라 보세요.
            </p>
            <div className="ed-font-grid">
              {NAME_FONTS.map((font) => {
                const active = (data.nameFont || 'default') === font.key;
                return (
                  <button
                    key={font.key}
                    type="button"
                    className={`ed-font-chip${active ? ' is-active' : ''}`}
                    onClick={() => patch('nameFont', font.key)}
                    aria-pressed={active}
                  >
                    <span
                      className="ed-font-sample"
                      style={font.stack ? { fontFamily: font.stack } : undefined}
                    >
                      도윤 &amp; 서연
                    </span>
                    <span className="ed-font-name">{font.name}</span>
                    <span className="ed-font-desc">{font.desc}</span>
                    <i
                      className={active ? 'ri-check-line ed-font-check' : 'ed-font-check'}
                      aria-hidden="true"
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="ed-field">
            <span className="ed-label">이름 미리보기</span>
            <div
              className="ed-sign-preview"
              style={
                nameFontStack(data.nameFont)
                  ? { fontFamily: nameFontStack(data.nameFont) }
                  : undefined
              }
            >
              {data.groom.name || '신랑'} &amp; {data.bride.name || '신부'}
            </div>
          </div>
        </div>
      </div>

      {/* CEREMONY */}
      <div className="ed-section">
        <div className="ed-section-head">
          <span className="step">2</span>
          <h2>예식 정보</h2>
          <span className="hint">날짜 · 시간 · 장소</span>
        </div>
        <div className="ed-cols">
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-date">
              예식 날짜
            </label>
            <input
              id="w-date"
              className="ed-input"
              type="date"
              value={data.date}
              onChange={(e) => patch('date', e.target.value)}
            />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-time">
              예식 시간
            </label>
            <input
              id="w-time"
              className="ed-input"
              value={data.time}
              onChange={(e) => patch('time', e.target.value)}
              placeholder="오후 1시 00분"
            />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-venue">
              예식장
            </label>
            <input
              id="w-venue"
              className="ed-input"
              value={data.venueName}
              onChange={(e) => patch('venueName', e.target.value)}
              placeholder="그랜드 하우스 서울"
            />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-hall">
              홀 / 층
            </label>
            <input
              id="w-hall"
              className="ed-input"
              value={data.hall}
              onChange={(e) => patch('hall', e.target.value)}
              placeholder="3층 그랜드홀"
            />
          </div>
        </div>
        <div className="ed-stack" style={{ marginTop: 14 }}>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-address">
              주소
            </label>
            <input
              id="w-address"
              className="ed-input"
              value={data.address}
              onChange={(e) => patch('address', e.target.value)}
              placeholder="서울특별시 강남구 테헤란로 152"
            />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-map">
              지도 검색어 (지도에 표시할 장소)
            </label>
            <input
              id="w-map"
              className="ed-input"
              value={data.mapQuery}
              onChange={(e) => patch('mapQuery', e.target.value)}
              placeholder="그랜드 하우스 서울"
            />
          </div>
        </div>
      </div>

      {/* GREETING */}
      <div className="ed-section">
        <div className="ed-section-head">
          <span className="step">3</span>
          <h2>신랑 · 신부의 인사</h2>
          <span className="hint">직접 적은 내용만 청첩장에 나와요</span>
        </div>
        <p className="ed-desc">
          청첩장에 전하고 싶은 글귀와 인사말을 적어 주세요. 적은 내용 그대로 청첩장에
          나오고, 비워 둔 칸은 표시되지 않아요.
        </p>
        <div className="ed-stack">
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-quote">
              글귀 (마음에 담고 싶은 한 줄)
            </label>
            <textarea
              id="w-quote"
              className="ed-textarea"
              value={data.quote}
              maxLength={500}
              onChange={(e) => patch('quote', e.target.value.slice(0, 500))}
              placeholder={'함께 걷는 이 길의 첫 문장을,\n소중한 분들과 나누고 싶습니다.'}
            />
            <span className="ed-count">{data.quote.length} / 500</span>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-greeting">
              인사말
            </label>
            <textarea
              id="w-greeting"
              className="ed-textarea"
              value={data.greeting}
              maxLength={500}
              onChange={(e) => patch('greeting', e.target.value.slice(0, 500))}
              placeholder={'저희 두 사람이 함께하는 새로운 시작에\n귀한 발걸음으로 축복해 주시면 감사하겠습니다.'}
            />
            <span className="ed-count">{data.greeting.length} / 500</span>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="w-sign">
              서명
            </label>
            <input
              id="w-sign"
              className="ed-input"
              value={data.greetingSign}
              onChange={(e) => patch('greetingSign', e.target.value)}
              placeholder="신랑 김도윤 · 신부 이서연"
            />
          </div>

          <div className="ed-field">
            <span className="ed-label">서명 폰트 (손글씨 느낌)</span>
            <p className="ed-font-hint">
              서명에 어울리는 손글씨 풍 글씨체를 골라 보세요. 바로 아래 미리보기에서 확인할 수 있어요.
            </p>
            <div className="ed-font-grid">
              {SIGNATURE_FONTS.map((font) => {
                const active = (data.signatureFont || 'default') === font.key;
                return (
                  <button
                    key={font.key}
                    type="button"
                    className={`ed-font-chip${active ? ' is-active' : ''}`}
                    onClick={() => patch('signatureFont', font.key)}
                    aria-pressed={active}
                  >
                    <span
                      className="ed-font-sample"
                      style={
                        font.stack ? { fontFamily: font.stack } : undefined
                      }
                    >
                      김도윤
                    </span>
                    <span className="ed-font-name">{font.name}</span>
                    <span className="ed-font-desc">{font.desc}</span>
                    <i
                      className={active ? 'ri-check-line ed-font-check' : 'ed-font-check'}
                      aria-hidden="true"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ed-field">
            <span className="ed-label">서명 미리보기</span>
            <div
              className={`ed-sign-preview${data.signatureFont && data.signatureFont !== 'default' ? ' is-script' : ''}`}
              style={
                signatureFontStack(data.signatureFont)
                  ? { fontFamily: signatureFontStack(data.signatureFont) }
                  : undefined
              }
            >
              {data.greetingSign || '신랑 김도윤 · 신부 이서연'}
            </div>
          </div>
        </div>
      </div>

      {/* ACCOUNTS */}
      <div className="ed-section">
        <div className="ed-section-head">
          <span className="step">4</span>
          <h2>마음 전하실 곳</h2>
          <span className="hint">계좌는 비워 두어도 괜찮아요</span>
        </div>        <div className="ed-cols">
          <div className="ed-stack">
            <p className="ed-subtitle">
              <i className="ri-bank-card-line" aria-hidden="true" />
              신랑 측
            </p>
            <div className="ed-field">
              <label className="ed-label" htmlFor="ga-bank">
                은행
              </label>
              <input
                id="ga-bank"
                className="ed-input"
                value={data.groomAccount.bank}
                onChange={(e) => patchAccount('groomAccount', 'bank', e.target.value)}
                placeholder="신한은행"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="ga-num">
                계좌번호
              </label>
              <input
                id="ga-num"
                className="ed-input"
                value={data.groomAccount.number}
                onChange={(e) => patchAccount('groomAccount', 'number', e.target.value)}
                placeholder="110-000-000000"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="ga-holder">
                예금주
              </label>
              <input
                id="ga-holder"
                className="ed-input"
                value={data.groomAccount.holder}
                onChange={(e) => patchAccount('groomAccount', 'holder', e.target.value)}
                placeholder="김정호"
              />
            </div>
          </div>

          <div className="ed-stack">
            <p className="ed-subtitle">
              <i className="ri-bank-card-line" aria-hidden="true" />
              신부 측
            </p>
            <div className="ed-field">
              <label className="ed-label" htmlFor="ba-bank">
                은행
              </label>
              <input
                id="ba-bank"
                className="ed-input"
                value={data.brideAccount.bank}
                onChange={(e) => patchAccount('brideAccount', 'bank', e.target.value)}
                placeholder="국민은행"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="ba-num">
                계좌번호
              </label>
              <input
                id="ba-num"
                className="ed-input"
                value={data.brideAccount.number}
                onChange={(e) => patchAccount('brideAccount', 'number', e.target.value)}
                placeholder="000-00-0000000"
              />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="ba-holder">
                예금주
              </label>
              <input
                id="ba-holder"
                className="ed-input"
                value={data.brideAccount.holder}
                onChange={(e) => patchAccount('brideAccount', 'holder', e.target.value)}
                placeholder="이광수"
              />
            </div>
          </div>
        </div>
      </div>

      {/* DIRECTIONS */}
      <div className="ed-section">
        <div className="ed-section-head">
          <span className="step">5</span>
          <h2>오시는 길 · 교통 안내</h2>
          <span className="hint">비워 두면 표시되지 않아요</span>
        </div>
        <p className="ed-desc">
          하객이 예식장으로 오는 방법을 적어 주세요. 자가용·버스·지하철 칸을 각각 채우면 청첩장에
          그대로 안내돼요.
        </p>
        <div className="ed-stack">
          <div className="ed-field">
            <label className="ed-label" htmlFor="tr-car">
              자가용 · 주차 안내
            </label>
            <textarea
              id="tr-car"
              className="ed-textarea"
              value={data.transit.car}
              maxLength={300}
              onChange={(e) => patchTransit('car', e.target.value.slice(0, 300))}
              placeholder={'내비게이션: “OO웨딩홀” 검색\n예식장 지하 주차장 2시간 무료'}
            />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="tr-bus">
              버스 안내
            </label>
            <textarea
              id="tr-bus"
              className="ed-textarea"
              value={data.transit.bus}
              maxLength={300}
              onChange={(e) => patchTransit('bus', e.target.value.slice(0, 300))}
              placeholder={'간선 470 (시청역 하차) → 도보 5분\n지선 7011 (웨딩홀 정문 하차)'}
            />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="tr-subway">
              지하철 안내
            </label>
            <textarea
              id="tr-subway"
              className="ed-textarea"
              value={data.transit.subway}
              maxLength={300}
              onChange={(e) => patchTransit('subway', e.target.value.slice(0, 300))}
              placeholder={'2호선 강남역 3번 출구 도보 8분\n신분당선 강남역 5번 출구 도보 8분'}
            />
          </div>
        </div>
      </div>
    </>
  );
}